import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Shield, Users, AlertTriangle, Mail, BarChart3, MailWarning,
  Trash2, Star, BadgeCheck, Ban, ShieldOff, Search, X, Send, Loader2, Calendar,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../api';
import AppNav from '../components/AppNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};
const fmtAgo = (iso) => {
  if (!iso) return '—';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

// ---------- STATS ----------
const StatsGrid = ({ stats }) => {
  const items = [
    { label: 'Total Users', value: stats?.total_users, icon: Users, color: 'text-fnl-pink' },
    { label: 'Today', value: stats?.signups_today, icon: Calendar, color: 'text-emerald-400' },
    { label: 'This Week', value: stats?.signups_this_week, icon: BarChart3, color: 'text-sky-400' },
    { label: 'Founding (used / left)', value: `${stats?.founding_members ?? 0} / ${stats?.founding_slots_left ?? 0}`, icon: Star, color: 'text-amber-400' },
    { label: 'Active 24h', value: stats?.active_24h, icon: Shield, color: 'text-fuchsia-400' },
    { label: 'Verified', value: stats?.verified, icon: BadgeCheck, color: 'text-purple-200' },
    { label: 'With Profile', value: stats?.profiles_complete, icon: Users, color: 'text-purple-200' },
    { label: 'Reports Open', value: stats?.reports_open, icon: AlertTriangle, color: stats?.reports_open ? 'text-red-400' : 'text-purple-200' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-stats">
      {items.map((it) => (
        <div key={it.label} className="bg-fnl-purple/50 border border-purple-900/40 rounded-xl p-4">
          <div className="flex items-center gap-2 text-purple-300 text-[10px] font-bold uppercase tracking-wider mb-1.5">
            <it.icon className={`w-3.5 h-3.5 ${it.color}`} />{it.label}
          </div>
          <div className="text-2xl font-extrabold text-white">{it.value ?? '—'}</div>
        </div>
      ))}
    </div>
  );
};

// Simple bar sparkline
const GrowthChart = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="bg-fnl-purple/50 border border-purple-900/40 rounded-xl p-4 mt-4">
      <div className="flex items-center gap-2 text-purple-200 text-sm mb-3 font-semibold">
        <BarChart3 className="w-4 h-4 text-fnl-pink" />Signups — last {data.length} days
      </div>
      <div className="flex items-end gap-1 h-32" data-testid="admin-growth-chart">
        {data.map((d, i) => (
          <div key={d.date} className="flex-1 flex flex-col items-center justify-end" title={`${d.date}: ${d.count}`}>
            <div
              className="w-full bg-gradient-to-t from-fnl-pink to-fuchsia-400 rounded-t transition-all"
              style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count ? '4px' : '2px', opacity: d.count ? 1 : 0.25 }}
            />
            {i % 5 === 0 && <span className="text-[8px] text-purple-400 mt-1 rotate-45 origin-left">{d.date.slice(5)}</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- USERS ----------
const UsersTable = ({ onEmailUser }) => {
  const [data, setData] = useState({ rows: [], total: 0, page: 1, page_size: 25 });
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('');
  const [includeSeed, setIncludeSeed] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async (page = 1) => {
    setLoading(true);
    try {
      const r = await apiClient.get('/admin/users', {
        params: { q: q || undefined, filter: filter || undefined, page, include_seed: includeSeed },
      });
      setData(r.data);
    } catch (e) { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [filter, includeSeed]);

  const action = async (userId, action) => {
    if (action === 'delete' && !window.confirm('Permanently delete this user and all their data? This cannot be undone.')) return;
    try {
      if (action === 'delete') {
        await apiClient.delete(`/admin/users/${userId}`);
      } else {
        await apiClient.post(`/admin/users/${userId}/action`, { action });
      }
      toast.success('Done');
      load(data.page);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const pageCount = Math.max(1, Math.ceil(data.total / data.page_size));

  return (
    <div className="space-y-3" data-testid="admin-users-section">
      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-purple-300" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load(1)}
            placeholder="Search email or name..."
            data-testid="admin-users-search"
            className="pl-9 bg-fnl-purple/60 border-purple-800 text-white"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="bg-fnl-purple/60 border border-purple-800 text-white rounded-md px-3 h-10 text-sm"
          data-testid="admin-users-filter"
        >
          <option value="">All users</option>
          <option value="founding">Founding Members</option>
          <option value="unverified">Unverified email</option>
          <option value="no_profile">No profile yet</option>
          <option value="admins">Admins</option>
          <option value="banned">Banned</option>
        </select>
        <label className="flex items-center gap-2 text-purple-200 text-sm px-3 select-none">
          <input type="checkbox" checked={includeSeed} onChange={(e) => setIncludeSeed(e.target.checked)} />Include seed
        </label>
        <Button onClick={() => load(1)} className="bg-fnl-pink hover:bg-fnl-pink-light">Search</Button>
      </div>

      <div className="bg-fnl-purple/40 border border-purple-900/40 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-fnl-purple/70 text-purple-300 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">Name / Email</th>
                <th className="px-3 py-2 text-left">Joined</th>
                <th className="px-3 py-2 text-left">IP (signup / last)</th>
                <th className="px-3 py-2 text-left">Last seen</th>
                <th className="px-3 py-2 text-left">Flags</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="6" className="p-6 text-center text-purple-300"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              )}
              {!loading && data.rows.length === 0 && (
                <tr><td colSpan="6" className="p-6 text-center text-purple-300">No users match.</td></tr>
              )}
              {data.rows.map((u) => (
                <tr key={u.id} className="border-t border-purple-900/30 hover:bg-fnl-purple/30" data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-white">{u.name || <span className="text-purple-400 italic">—</span>}</div>
                    <div className="text-xs text-purple-300">{u.email}</div>
                  </td>
                  <td className="px-3 py-2 text-purple-200 text-xs whitespace-nowrap">{fmtDate(u.created_at)}</td>
                  <td className="px-3 py-2 text-purple-200 text-xs font-mono">
                    <div>{u.signup_ip || '—'}</div>
                    <div className="text-purple-400">{u.last_login_ip || '—'}</div>
                  </td>
                  <td className="px-3 py-2 text-purple-200 text-xs whitespace-nowrap">{fmtAgo(u.last_seen)}</td>
                  <td className="px-3 py-2 text-xs space-y-0.5">
                    {u.is_admin && <span className="inline-block px-1.5 py-0.5 rounded bg-fnl-pink/30 text-fnl-pink mr-1">ADMIN</span>}
                    {u.founding_member && <span className="inline-block px-1.5 py-0.5 rounded bg-amber-400/20 text-amber-300 mr-1">★ FND</span>}
                    {u.email_verified && <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-400/15 text-emerald-300 mr-1">✓</span>}
                    {u.banned && <span className="inline-block px-1.5 py-0.5 rounded bg-red-500/30 text-red-300 mr-1">BANNED</span>}
                    {!u.has_profile && <span className="inline-block px-1.5 py-0.5 rounded bg-purple-900/40 text-purple-300 mr-1">no profile</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button title="Email this user" onClick={() => onEmailUser(u)} className="p-1.5 rounded hover:bg-fnl-pink/20 text-fnl-pink" data-testid={`admin-email-${u.id}`}><Mail className="w-3.5 h-3.5" /></button>
                      {!u.email_verified && <button title="Mark email verified" onClick={() => action(u.id, 'verify_email')} className="p-1.5 rounded hover:bg-emerald-500/20 text-emerald-300"><BadgeCheck className="w-3.5 h-3.5" /></button>}
                      {!u.founding_member ? (
                        <button title="Grant Founding" onClick={() => action(u.id, 'grant_founding')} className="p-1.5 rounded hover:bg-amber-400/20 text-amber-300"><Star className="w-3.5 h-3.5" /></button>
                      ) : (
                        <button title="Revoke Founding" onClick={() => action(u.id, 'revoke_founding')} className="p-1.5 rounded hover:bg-amber-400/20 text-amber-300/60"><Star className="w-3.5 h-3.5" /></button>
                      )}
                      {u.banned ? (
                        <button title="Unban" onClick={() => action(u.id, 'unban')} className="p-1.5 rounded hover:bg-emerald-500/20 text-emerald-300"><ShieldOff className="w-3.5 h-3.5" /></button>
                      ) : (
                        <button title="Ban" onClick={() => action(u.id, 'ban')} className="p-1.5 rounded hover:bg-red-500/20 text-red-300"><Ban className="w-3.5 h-3.5" /></button>
                      )}
                      <button title="Delete user" onClick={() => action(u.id, 'delete')} className="p-1.5 rounded hover:bg-red-500/20 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-3 py-2 border-t border-purple-900/30 text-xs text-purple-300">
          <span>{data.total} total</span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" disabled={data.page <= 1} onClick={() => load(data.page - 1)} className="text-fnl-pink">← Prev</Button>
            <span>Page {data.page} / {pageCount}</span>
            <Button variant="ghost" size="sm" disabled={data.page >= pageCount} onClick={() => load(data.page + 1)} className="text-fnl-pink">Next →</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- EMAIL COMPOSER ----------
const EmailComposer = ({ singleUser, onClose }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState('all');
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!subject.trim() || !body.trim()) return toast.error('Subject and body are required');
    if (!singleUser && !window.confirm(`Send this email to all "${audience}" users? This cannot be undone.`)) return;
    setSending(true);
    try {
      if (singleUser) {
        const r = await apiClient.post(`/admin/users/${singleUser.id}/email`, { subject, body_html: body });
        toast.success(r.data.ok ? `Sent to ${singleUser.email}` : 'Send failed');
        if (r.data.ok) onClose?.();
      } else {
        const r = await apiClient.post('/admin/email/blast', {
          subject, body_html: body, audience,
        });
        toast.success(`Sent to ${r.data.sent} of ${r.data.audience_size} (failed: ${r.data.failed})`);
        setSubject(''); setBody('');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <div className="bg-fnl-purple/40 border border-purple-900/40 rounded-xl p-5 space-y-4" data-testid="admin-email-composer">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-white">
          {singleUser ? `Email ${singleUser.email}` : 'Email blast'}
        </h3>
        {singleUser && <button onClick={onClose} className="text-purple-300 hover:text-white"><X className="w-5 h-5" /></button>}
      </div>
      {!singleUser && (
        <div>
          <label className="text-purple-200 text-xs uppercase tracking-wider font-semibold mb-1 block">Audience</label>
          <select value={audience} onChange={(e) => setAudience(e.target.value)} className="w-full bg-fnl-dark/60 border border-purple-800 text-white rounded-md px-3 h-10 text-sm" data-testid="admin-email-audience">
            <option value="all">All users (no seed)</option>
            <option value="founding">Founding members only</option>
            <option value="verified">Verified emails only</option>
            <option value="unverified">Unverified emails only</option>
            <option value="no_profile">No profile yet</option>
          </select>
        </div>
      )}
      <div>
        <label className="text-purple-200 text-xs uppercase tracking-wider font-semibold mb-1 block">Subject</label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="What's new on Flirty 💖" data-testid="admin-email-subject" className="bg-fnl-dark/60 border-purple-800 text-white" />
      </div>
      <div>
        <label className="text-purple-200 text-xs uppercase tracking-wider font-semibold mb-1 block">Body — HTML allowed</label>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} placeholder="Hey Flirty crew,&#10;&#10;We just shipped a big update..." data-testid="admin-email-body" className="bg-fnl-dark/60 border-purple-800 text-white font-mono text-sm" />
        <p className="text-xs text-purple-400 mt-1">Tip: wrap paragraphs in &lt;p&gt; tags. The Flirty branding shell + footer is added automatically.</p>
      </div>
      <Button onClick={send} disabled={sending} className="w-full bg-fnl-pink hover:bg-fnl-pink-light text-white font-semibold" data-testid="admin-email-send">
        {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
        {singleUser ? 'Send' : 'Send blast'}
      </Button>
    </div>
  );
};

// ---------- MODERATION ----------
const ModerationPanel = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { setReports((await apiClient.get('/admin/moderation/reports')).data); }
    catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const ban = async (uid) => {
    try { await apiClient.post(`/admin/users/${uid}/action`, { action: 'ban' }); toast.success('Banned'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Ban failed'); }
  };
  if (loading) return <div className="text-center py-10 text-purple-300"><Loader2 className="inline w-5 h-5 animate-spin" /></div>;
  if (reports.length === 0) return <div className="text-center py-10 text-purple-300" data-testid="admin-moderation-empty">No open reports.</div>;
  return (
    <div className="space-y-2" data-testid="admin-moderation-list">
      {reports.map((r) => (
        <div key={r.user_id} className="flex items-center gap-3 p-3 bg-fnl-purple/40 border border-purple-900/40 rounded-lg">
          {r.photo_base64 ? (
            <img src={r.photo_base64} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : <div className="w-10 h-10 rounded-full bg-fnl-purple-light/40" />}
          <div className="flex-1 min-w-0">
            <div className="text-white font-semibold truncate">{r.name || r.email}</div>
            <div className="text-xs text-purple-300">{r.email}</div>
            <div className="text-xs text-purple-200 mt-0.5">
              <span className="text-red-300 font-bold">{r.count} report{r.count > 1 ? 's' : ''}</span> · {r.reasons.join(', ')}
              {r.latest_details && <> · "<span className="italic">{r.latest_details}</span>"</>}
            </div>
          </div>
          <span className="text-xs text-purple-400 whitespace-nowrap">{fmtAgo(r.last_reported)}</span>
          {!r.banned && <Button size="sm" variant="ghost" onClick={() => ban(r.user_id)} className="text-red-300 hover:bg-red-500/20"><Ban className="w-3.5 h-3.5 mr-1" />Ban</Button>}
        </div>
      ))}
    </div>
  );
};

// ---------- EMAIL LOG ----------
const EmailLogPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiClient.get('/admin/email-log').then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="text-center py-10 text-purple-300"><Loader2 className="inline w-5 h-5 animate-spin" /></div>;
  return (
    <div className="bg-fnl-purple/40 border border-purple-900/40 rounded-xl overflow-x-auto" data-testid="admin-email-log">
      <table className="w-full text-sm">
        <thead className="bg-fnl-purple/70 text-purple-300 text-[10px] uppercase">
          <tr><th className="px-3 py-2 text-left">Sent</th><th className="px-3 py-2 text-left">Kind</th><th className="px-3 py-2 text-left">To</th><th className="px-3 py-2 text-left">Subject</th></tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan="4" className="p-6 text-center text-purple-300">No emails sent yet.</td></tr>}
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-purple-900/30">
              <td className="px-3 py-2 text-purple-200 text-xs whitespace-nowrap">{fmtAgo(r.sent_at)}</td>
              <td className="px-3 py-2 text-fnl-pink text-xs font-bold">{r.kind || '—'}</td>
              <td className="px-3 py-2 text-purple-200 text-xs">{r.to || (r.user_id?.slice(0, 8) || '—')}</td>
              <td className="px-3 py-2 text-purple-100 text-sm truncate max-w-md">{r.subject || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ---------- PAGE ----------
const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [growth, setGrowth] = useState([]);
  const [singleEmailUser, setSingleEmailUser] = useState(null);

  useEffect(() => {
    if (!user?.is_admin) return;
    apiClient.get('/admin/stats').then((r) => setStats(r.data)).catch(() => {});
    apiClient.get('/admin/growth').then((r) => setGrowth(r.data)).catch(() => {});
  }, [user]);

  if (user === null) return null;
  if (!user?.is_admin) return <Navigate to="/browse" replace />;

  const tabs = [
    { id: 'stats', label: 'Overview', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'moderation', label: 'Moderation', icon: AlertTriangle },
    { id: 'email', label: 'Email Blast', icon: Mail },
    { id: 'log', label: 'Email Log', icon: MailWarning },
  ];

  return (
    <div className="min-h-screen bg-fnl-dark">
      <AppNav />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-fnl-pink/20 border border-fnl-pink/40 flex items-center justify-center">
            <Shield className="w-5 h-5 text-fnl-pink" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Admin Console</h1>
            <p className="text-purple-300 text-sm">Signed in as <span className="text-fnl-pink">{user.email}</span></p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1 mb-5 border-b border-purple-900/40">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-2.5 text-sm font-semibold flex items-center gap-2 border-b-2 -mb-px transition ${
                tab === t.id
                  ? 'border-fnl-pink text-fnl-pink'
                  : 'border-transparent text-purple-300 hover:text-white'
              }`}
            >
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>

        {tab === 'stats' && (
          <>
            <StatsGrid stats={stats} />
            {growth.length > 0 && <GrowthChart data={growth} />}
          </>
        )}
        {tab === 'users' && <UsersTable onEmailUser={(u) => { setSingleEmailUser(u); setTab('email'); }} />}
        {tab === 'moderation' && <ModerationPanel />}
        {tab === 'email' && (
          <EmailComposer
            singleUser={singleEmailUser}
            onClose={() => { setSingleEmailUser(null); setTab('users'); }}
          />
        )}
        {tab === 'log' && <EmailLogPanel />}
      </div>
    </div>
  );
};

export default AdminPage;
